import { StringUtility } from '@common/utils';
import { DateTime } from 'luxon';
import {
  HospitalModel,
  IHospitalModel,
  IModel,
  IPetModel,
  IPronounModel,
  IReferralTypeModel,
  IRelationshipType,
  IStateModel,
  Model,
  PetModel,
  PronounModel,
  ReferralTypeModel,
  RelationshipTypeModel,
  StateModel,
  IClientNoteModel,
  ClientNoteModel,
  IClientAuthorizedUsersModel,
  ClientAuthorizedUsersModel,
} from '.';
import { computed, makeObservable, observable } from 'mobx';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';
export interface IClientModel extends ISyncModel {
  id?: number;
  clientId: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  emailAddressNotAvailable: boolean;
  cellPhone: string | undefined;
  homePhone: string | undefined;
  alternatePhone: string | undefined;
  address: string;
  address2: string | undefined;
  city: string;
  stateId: number | undefined;
  county: string;
  zipCode: string;
  isOKToSMS: boolean;
  palsNumber: string | undefined;
  dateCreated: string;
  dateUpdated: string;
  lastVisitDate: string | undefined;
  firstVisitDate: string | undefined;
  alternatePetParentName: string | undefined;
  pets: IPetModel[];
  state: IStateModel | undefined;
  referralTypeId: number | undefined;
  referralType: IReferralTypeModel | undefined;
  isDeleted: boolean;
  pgrMasterId: string | undefined;
  businessName: string | undefined;
  isActive: boolean;
  hospitalId: number | undefined;
  primaryHospital: Omit<IHospitalModel, 'defaultScheduleTemplate'> | undefined;
  pronounId: number | undefined;
  pronoun: IPronounModel | undefined;
  relationshipTypeId: number | undefined;
  relationshipType: IRelationshipType | undefined;
  canSMS: boolean | undefined;
  canSMSDateUpdated: string | undefined;
  canSMSUpdatedUserTitle: string | undefined;
  canSMSUpdatedUserFirstName: string | undefined;
  canSMSUpdatedUserLastName: string | undefined;
  canCall: boolean | undefined;
  canCallDateUpdated: string | undefined;
  canCallUpdatedUserTitle: string | undefined;
  canCallUpdatedUserFirstName: string | undefined;
  canCallUpdatedUserLastName: string | undefined;
  nationwideId: string | undefined;
  nationwideIdNotAvailable: boolean | undefined;
  notes: IClientNoteModel[];
  clientAuthorizedUsers: IClientAuthorizedUsersModel[];
}
export interface ClientPhone {
  cellPhone: string | undefined;
  homePhone: string | undefined;
  alternatePhone: string | undefined;
}
export class ClientModel
    extends SyncModel
  implements Record<keyof IClientModel, any>
{
  @observable id: number | undefined;
  @observable clientId: number;
  @observable firstName: string;
  @observable lastName: string;
  @observable emailAddress: string;
  @observable emailAddressNotAvailable: boolean;
  @observable cellPhone: string | undefined;
  @observable homePhone: string | undefined;
  @observable alternatePhone: string | undefined;
  @observable address: string;
  @observable address2: string | undefined;
  @observable city: string;
  @observable stateId: number | undefined;
  @observable county: string;
  @observable zipCode: string;
  @observable isOKToSMS: boolean;
  @observable palsNumber: string | undefined;
  @observable dateCreated: string;
  @observable dateUpdated: string;
  @observable lastVisitDate: string | undefined;
  @observable firstVisitDate: string | undefined;
  @observable alternatePetParentName: string | undefined;
  @observable pets: IPetModel[];
  @observable state: IStateModel | undefined;
  @observable referralTypeId: number | undefined;
  @observable referralType: IReferralTypeModel | undefined;
  @observable isDeleted: boolean;
  @observable pgrMasterId: string | undefined;
  @observable businessName: string | undefined;
  @observable isActive: boolean;
  hospitalId: number | undefined;
  primaryHospital: Omit<HospitalModel, 'defaultScheduleTemplate'> | undefined;
  @observable pronounId: number | undefined;
  @observable pronoun: IPronounModel | undefined;
  @observable relationshipTypeId: number | undefined;
  @observable relationshipType: IRelationshipType | undefined;
  @observable canSMS: boolean | undefined;
  @observable canSMSDateUpdated: DateTime | undefined;
  @observable canSMSUpdatedUserTitle: string | undefined;
  @observable canSMSUpdatedUserFirstName: string | undefined;
  @observable canSMSUpdatedUserLastName: string | undefined;
  @observable canCall: boolean | undefined;
  @observable canCallDateUpdated: DateTime | undefined;
  @observable canCallUpdatedUserTitle: string | undefined;
  @observable canCallUpdatedUserFirstName: string | undefined;
  @observable canCallUpdatedUserLastName: string | undefined;
  @observable nationwideId: string | undefined;
  @observable nationwideIdNotAvailable: boolean | undefined;
  @observable notes: IClientNoteModel[] = [];
  @observable clientAuthorizedUsers: IClientAuthorizedUsersModel[] = [];

  constructor(params: IClientModel) {
    super(params);
    this.id = params.id;
    this.clientId = params.clientId;
    this.firstName = params.firstName;
    this.lastName = params.lastName;
    this.emailAddress = params.emailAddress;
    this.emailAddressNotAvailable = params.emailAddressNotAvailable;
    this.cellPhone = StringUtility.SanitizeString(params.cellPhone);
    this.homePhone = StringUtility.SanitizeString(params.homePhone);
    this.alternatePhone = StringUtility.SanitizeString(params.alternatePhone);
    this.alternatePetParentName = StringUtility.SanitizeString(
      params.alternatePetParentName,
    );
    this.businessName = StringUtility.SanitizeString(params.businessName);
    this.address = params.address;
    this.address2 = StringUtility.SanitizeString(params.address2);
    this.city = params.city;
    this.stateId = StringUtility.SanitizeNumber(params.stateId);
    this.state = params.state ? new StateModel(params.state) : undefined;
    this.county = params.county;
    this.zipCode = params.zipCode;
    this.isOKToSMS = params.isOKToSMS;
    this.palsNumber = StringUtility.SanitizeString(params.palsNumber);
    this.dateCreated = params.dateCreated;
    this.lastVisitDate = params.lastVisitDate;
    this.dateUpdated = params.dateUpdated;
    this.pets = (params.pets ?? []).map((p) => new PetModel(p));
    this.referralTypeId = StringUtility.SanitizeNumber(params.referralTypeId);
    this.referralType = params.referralType
      ? new ReferralTypeModel(params.referralType)
      : undefined;
    this.isDeleted = params.isDeleted;
    this.isActive = params.isActive;
    this.pgrMasterId = StringUtility.SanitizeString(params.pgrMasterId);
    this.hospitalId = StringUtility.SanitizeNumber(params.hospitalId);
    this.primaryHospital = params.primaryHospital
      ? new HospitalModel(params.primaryHospital as IHospitalModel)
      : undefined;
    this.pronounId = StringUtility.SanitizeNumber(params.pronounId);
    this.pronoun = params.pronoun
      ? new PronounModel(params.pronoun)
      : undefined;
    this.relationshipTypeId = StringUtility.SanitizeNumber(
      params.relationshipTypeId,
    );
    this.relationshipType = params.relationshipType
      ? new RelationshipTypeModel(params.relationshipType)
      : undefined;
    this.canSMS = _.defaultTo(params?.canSMS, true);
    this.canSMSDateUpdated = params?.canSMSDateUpdated
      ? StringUtility.SanitizeDateTimeISOAsUtc(
          params?.canSMSDateUpdated.toString(),
        )
      : undefined;
    this.canSMSUpdatedUserTitle = _.defaultTo(
      params?.canSMSUpdatedUserTitle,
      '',
    );
    this.canSMSUpdatedUserFirstName = _.defaultTo(
      params?.canSMSUpdatedUserFirstName,
      '',
    );
    this.canSMSUpdatedUserLastName = _.defaultTo(
      params?.canSMSUpdatedUserLastName,
      '',
    );
    this.canCall = _.defaultTo(params?.canCall, true);
    this.canCallDateUpdated = params?.canCallDateUpdated
      ? StringUtility.SanitizeDateTimeISOAsUtc(
          params?.canCallDateUpdated.toString(),
        )
      : undefined;
    this.canCallUpdatedUserTitle = _.defaultTo(
      params?.canCallUpdatedUserTitle,
      '',
    );
    this.canCallUpdatedUserFirstName = _.defaultTo(
      params?.canCallUpdatedUserFirstName,
      '',
    );
    this.canCallUpdatedUserLastName = _.defaultTo(
      params?.canCallUpdatedUserLastName,
      '',
    );
    this.nationwideId = _.defaultTo(params?.nationwideId, '');
    this.nationwideIdNotAvailable = _.defaultTo(
      params?.nationwideIdNotAvailable,
      false,
    );
    this.notes = (params.notes ?? []).map((n) => new ClientNoteModel(n));
    this.clientAuthorizedUsers = (params.clientAuthorizedUsers ?? []).map(
      (cau) => new ClientAuthorizedUsersModel(cau),
    );
    makeObservable(this);
  }

  @computed
  get fullName() {
    return `${this.firstName ? this.firstName + ' ' : ''}${this.lastName ?? ''}`;
  }

  @computed
  get canCallFullName() {
    if (this.canCallUpdatedUserFirstName && this.canCallUpdatedUserLastName)
      return StringUtility.formattedUserName(
        this.canCallUpdatedUserFirstName,
        this.canCallUpdatedUserLastName,
        this.canCallUpdatedUserTitle,
      );
    else return undefined;
  }

  @computed
  get canSMSFullName() {
    if (this.canSMSUpdatedUserFirstName && this.canSMSUpdatedUserLastName)
      return StringUtility.formattedUserName(
        this.canSMSUpdatedUserFirstName,
        this.canSMSUpdatedUserLastName,
        this.canSMSUpdatedUserTitle,
      );
    else return undefined;
  }
}
